import api from '../api/client';
import React, { useState, useEffect, useCallback } from 'react';
import {
    Form,
    InputNumber,
    Input,
    Select,
    Button,
    Upload,
    DatePicker,
    Divider,
    Space,
    Card,
    Table,
    Typography,
    message,
    Row,
    Col,
    Spin,
    Tag,
    Modal,
    Alert,
    Badge,
    Steps,
    Tooltip
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    UploadOutlined,
    PrinterOutlined,
    ReloadOutlined,
    ShoppingOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    UserAddOutlined,
    EggOutlined,
    NumberOutlined,
    FastForwardFilled,
    CreditCardOutlined,
    WalletOutlined,
    HistoryOutlined
} from '@ant-design/icons';
import moment from 'moment';
import config from '../config';
import { formatKES } from '../utils/currency';
import { useSelector } from 'react-redux';
import { useFarmProfile } from '../contexts/FarmProfileContext';
import dayjs from 'dayjs';
import { groupAccountOptions } from '../utils/account';
import { useHistory } from 'react-router-dom';

const { Title, Text } = Typography;
const { Step } = Steps;
const { Option } = Select;

export default function DynamicSalesForm({ stakeholders: initialStakeholders, farms, onChange, employees }) {
    const history = useHistory();
    const [form] = Form.useForm();
    const [deliveredMethod, setDeliveredMethod] = useState('picked');
    const [items, setItems] = useState([]);
    const [availableAssets, setAvailableAssets] = useState([]);
    const [assetTypes, setAssetTypes] = useState([]);
    const [eggInventoryData, setEggInventoryData] = useState([]);
    const [loadingAssets, setLoadingAssets] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [receiptData, setReceiptData] = useState(null);
    const [showSuccessReceipt, setShowSuccessReceipt] = useState(false);
    const [stakeholders, setStakeholders] = useState(initialStakeholders || []);
    const [customerModalVisible, setCustomerModalVisible] = useState(false);
    const [customerForm] = Form.useForm();
    const [addingCustomer, setAddingCustomer] = useState(false);
    const [customerCredit, setCustomerCredit] = useState(0);
    const [creditDetails, setCreditDetails] = useState(null);
    const [useCredit, setUseCredit] = useState(0);
    const [showCreditModal, setShowCreditModal] = useState(false);
    const [creditLoading, setCreditLoading] = useState(false);
    const [creditHistory, setCreditHistory] = useState([]);
    const [showCreditHistory, setShowCreditHistory] = useState(false);
    const [addCreditModalVisible, setAddCreditModalVisible] = useState(false);
    const [addCreditForm] = Form.useForm();
    const [paidAccounts, setPaidAccounts] = useState([]);
    const account = useSelector((state) => state.account.user);
    const { profile } = useFarmProfile();
    const [saleType, setSaleType] = useState('full');
    const [wasteModalVisible, setWasteModalVisible] = useState(false);
    const [availableWaste, setAvailableWaste] = useState([]);
    const [selectedWasteRows, setSelectedWasteRows] = useState([]);
    const [loadingWaste, setLoadingWaste] = useState(false);
    const [wasteSearch, setWasteSearch] = useState('');
    const [wastePagination, setWastePagination] = useState({ page: 1, limit: 20, total: 0 });

    // Unique key for a waste row: slaughterId + waste type name (e.g. "12-Hooves")
    const wasteRowKey = (slaughterId, name) => `${slaughterId}-${name}`;

    const fetchUnrecordedWaste = async (page = 1, search = wasteSearch) => {
        setLoadingWaste(true);
        try {
            const farmId = form.getFieldValue('farmId');
            const params = {
                farm_id: farmId || undefined,
                search: search || undefined,
                page: page || 1,
                limit: 20
            };
            Object.keys(params).forEach((key) => params[key] === undefined && delete params[key]);
            const response = await api.get('/api/slaughter/waste/unrecorded', { params });
            const fetchedWaste = response.data.data || [];

            // Exclude waste rows already staged in the current sale (not yet submitted,
            // so the backend's "unrecorded" list doesn't know about them yet).
            const stagedWasteKeys = new Set(
                items.filter((i) => i.type_slug === 'waste').map((i) => wasteRowKey(i.slaughterId, i.wasteName))
            );
            const filteredWaste = fetchedWaste.filter((w) => !stagedWasteKeys.has(wasteRowKey(w.slaughterId, w.name)));

            setAvailableWaste(filteredWaste);
            setWastePagination({
                page,
                limit: 20,
                total: response.data.pagination?.total || 0
            });
            setWasteModalVisible(true);
        } catch (error) {
            const errMsg = error?.response?.data?.error || error?.message || 'Failed to load biological items';
            console.error('Error fetching waste:', errMsg);
            message.error(errMsg);
        } finally {
            setLoadingWaste(false);
        }
    };

    // Add selected waste to sale items
    const addSelectedWasteToSale = () => {
        if (selectedWasteRows.length === 0) {
            message.warning('Select at least one Biological Item');
            return;
        }
        
        const newItems = selectedWasteRows.map((w) => ({
            id: Date.now() + Math.random() + w.slaughterId + w.name,
            type_slug: 'waste',
            wasteName: w.name,
            weight: w.weight, // max available (from /unrecorded, already = remaining)
            weightToSell: w.weight, // <-- new: editable, defaults to full available
            pricePerUnit: 0,
            quantity: 1,
            pricingMethod: 'weight',
            slaughterId: w.slaughterId,
            animalId: w.animalId,
            tagNumber: w.tagNumber,
            animalName: w.animalName,
            isWaste: true
        }));
        setItems([...items, ...newItems]);
        setWasteModalVisible(false);
        setSelectedWasteRows([]);
        message.success(`${newItems.length} Biological Item(s) added to sale`);
    };

    // Fetch paid-through accounts for the form (used in select options)
    const fetchPaidAccounts = useCallback(async () => {
        try {
            const { data } = await api.get(`/api/paid-through`);
            setPaidAccounts(data);
        } catch (error) {
            console.error('Failed to fetch paid-through accounts', error);
        }
    }, [api]);

    useEffect(() => {
        fetchPaidAccounts();
    }, [fetchPaidAccounts]);

    const paidOptions = groupAccountOptions(paidAccounts); // from your utils

    // Fetch all available assets dynamically
    const fetchAvailableAssets = async () => {
        setLoadingAssets(true);
        try {
            const response = await api.get(`/available-assets-weight`, {
                params: {
                    status: 'alive,active,slaughtered',
                    archived: false
                }
            });
            setAvailableAssets(response.data);
        } catch (error) {
            console.error('Error fetching assets:', error);
            message.error('Failed to load available assets');
        } finally {
            setLoadingAssets(false);
        }
    };

    // Fetch egg inventory
    const fetchEggInventory = async () => {
        try {
            const response = await api.get(`/egg-inventory`);
            setEggInventoryData(response.data);
        } catch (error) {
            console.error('Error fetching egg inventory:', error);
        }
    };

    // Fetch asset types configuration
    const fetchAssetTypes = async () => {
        try {
            const response = await api.get(`/asset-types-config`);
            setAssetTypes(response.data);
        } catch (error) {
            console.error('Error fetching asset types:', error);
        }
    };

    // Fetch customers
    const fetchCustomers = async () => {
        try {
            const response = await api.get(`/customer`, {
                params: { type: 'customer' }
            });
            setStakeholders(response.data);
        } catch (error) {
            console.error('Error fetching customers:', error);
        }
    };

    // Fetch customer credit balance
    const fetchCustomerCredit = async (customerId) => {
        if (!customerId) return;
        try {
            const response = await api.get(`/customer-credits/${customerId}/balance`);
            if (response.data.success) {
                setCustomerCredit(response.data.data.total_credit);
                setCreditDetails(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching credit:', error);
            setCustomerCredit(0);
        }
    };

    // Fetch credit history
    const fetchCreditHistory = async (customerId) => {
        if (!customerId) return;
        try {
            const response = await api.get(`/customer-credits/${customerId}/history`);
            if (response.data.success) {
                setCreditHistory(response.data.data.credits || []);
                setShowCreditHistory(true);
            }
        } catch (error) {
            console.error('Error fetching credit history:', error);
            message.error('Failed to load credit history');
        }
    };

    // Add new credit manually
    const handleAddCredit = async (values) => {
        setCreditLoading(true);
        try {
            const customerId = form.getFieldValue('stakeholderId');
            const response = await api.post(`/customer-credits`, {
                customer_id: customerId,
                amount: values.amount,
                payment_method: values.payment_method,
                payment_date: values.payment_date.format('YYYY-MM-DD'),
                notes: values.notes,
                expiry_days: values.expiry_days,
                transaction_type: 'prepayment',
                user_id: account?.id,
                user_name: account?.firstname,
                debitAccount: values.debitAccount
            });

            if (response.data.success) {
                message.success(`KES ${values.amount.toLocaleString()} added as customer credit`);
                addCreditForm.resetFields();
                setAddCreditModalVisible(false);
                fetchCustomerCredit(customerId);
            }
        } catch (error) {
            console.error('Error adding credit:', error);
            message.error('Failed to add credit');
        } finally {
            setCreditLoading(false);
        }
    };

    // Apply credit to sale
    const applyCredit = () => {
        if (useCredit <= 0) {
            message.error('Please enter a valid credit amount');
            return;
        }
        if (useCredit > customerCredit) {
            message.error(`Credit amount cannot exceed available balance of ${formatKES(customerCredit)}`);
            return;
        }

        form.setFieldsValue({ credit_to_apply: useCredit });
        message.success(`Applied KES ${useCredit.toLocaleString()} from customer credit`);
        setShowCreditModal(false);
        setUseCredit(0);
    };

    // Add new customer
    const handleAddCustomer = async (values) => {
        setAddingCustomer(true);
        try {
            const response = await api.post(`/stakeholders`, {
                ...values,
                name: values.first_name + ' ' + values.last_name,
                type: 'customer',
                userId: account?.id,
                userName: account?.firstname
            });

            if (response.data.success) {
                message.success('Customer added successfully');
                customerForm.resetFields();
                setCustomerModalVisible(false);
                await fetchCustomers();
                form.setFieldsValue({ stakeholderId: response.data.stakeholder.id });
                fetchCustomerCredit(response.data.stakeholder.id);
            }
        } catch (error) {
            console.error('Error adding customer:', error);
            message.error(error.response?.data?.error || 'Failed to add customer');
        } finally {
            setAddingCustomer(false);
        }
    };

    useEffect(() => {
        fetchAvailableAssets();
        fetchAssetTypes();
        fetchCustomers();
        fetchEggInventory();
    }, []);

    // Monitor customer selection to fetch credit
    const handleCustomerChange = (customerId) => {
        form.setFieldsValue({ stakeholderId: customerId, credit_to_apply: 0 });
        fetchCustomerCredit(customerId);
        setUseCredit(0);
    };

    // Get assets by type
    const getAssetsByType = (typeSlug) => {
        return availableAssets.filter((asset) => asset.type_slug === typeSlug);
    };

    // Get asset type config
    const getAssetTypeConfig = (typeSlug) => {
        return assetTypes.find((t) => t.slug === typeSlug);
    };

    // Check if item is eggs
    const isEggItem = (item) => {
        return item.type_slug === 'eggs' || (item.assetId && availableAssets.find((a) => a.id === item.assetId)?.type_slug === 'eggs');
    };

    const calculateSubtotal = (item) => {
        if (isEggItem(item)) {
            return (item.quantity || 0) * (item.pricePerUnit || 0);
        }

        // calculateSubtotal
        if (item.type_slug === 'waste') {
            return (item.weightToSell || 0) * (item.pricePerUnit || 0); // was item.weight
        }

        const selectedAsset = availableAssets.find((a) => a.id === item.assetId);
        const currentWeight = selectedAsset?.current_weight || selectedAsset?.weight || 0;

        // Handle weight-based pricing with partial sale
        if (item.pricingMethod === 'weight' && item.saleType === 'partial_weight') {
            return (item.weightToSell || 0) * (item.pricePerUnit || 0);
        }

        // Handle weight-based pricing with full animal sale
        if (item.pricingMethod === 'weight' && item.saleType === 'full_weight') {
            return (item.quantity || 1) * currentWeight * (item.pricePerUnit || 0);
        }

        // Handle quantity-based pricing (simple)
        return (item.quantity || 0) * (item.pricePerUnit || 0);
    };

    const isCurrentItemComplete = (item) => {
        if (!item.type_slug) return false;

        if (isEggItem(item)) {
            return item.eggType && item.quantity > 0 && item.pricePerUnit > 0;
        }

        if (item.type_slug === 'waste') {
            return item.weightToSell > 0 && item.weightToSell <= item.weight && item.pricePerUnit > 0;
        }

        if (!item.assetId) return false;

        // Handle partial weight validation
        if (item.saleType === 'partial_weight') {
            return item.weightToSell > 0 && item.pricePerUnit > 0;
        }

        // Handle full weight validation
        if (item.saleType === 'full_weight') {
            return item.quantity > 0 && item.weightPerAnimal > 0 && item.pricePerUnit > 0;
        }

        // Handle weight-based pricing validation
        if (item.pricingMethod === 'weight') {
            return item.weight > 0 && item.quantity > 0 && item.pricePerUnit > 0;
        }

        // Handle quantity-based pricing validation
        return item.quantity > 0 && item.pricePerUnit > 0;
    };

    const areAllItemsComplete = () => {
        return items.length > 0 && items.every((item) => isCurrentItemComplete(item));
    };

    const isAddButtonDisabled = () => {
        if (items.length === 0) return false;
        return !isCurrentItemComplete(items[items.length - 1]);
    };

    const addItem = () => {
        if (isAddButtonDisabled()) {
            message.warning('Please complete all fields in the current item before adding another.');
            return;
        }

        const newItem = {
            id: Date.now() + Math.random(),
            type_slug: '',
            assetId: null,
            quantity: 1,
            pricePerUnit: 0,
            weight: 0,
            pricingMethod: 'quantity'
        };
        setItems([...items, newItem]);
    };

    const removeItem = (id) => {
        if (items.length === 1) {
            message.warning('At least one item is required');
            return;
        }
        setItems(items.filter((item) => item.id !== id));
    };

    const updateItem = (id, key, value) => {
        const updated = [...items];
        const itemIndex = updated.findIndex((item) => item.id === id);

        if (itemIndex === -1) return;

        if (key === 'type_slug') {
            updated[itemIndex] = {
                id: updated[itemIndex].id,
                type_slug: value,
                assetId: null,
                quantity: 1,
                pricePerUnit: 0,
                weight: 0,
                pricingMethod: 'quantity',
                batchId: null,
                eggType: null,
                brokenDuringSale: 0,
                doubleYolkDuringSale: 0,
                mediumDuringSale: 0,
                spoiltDuringSale: 0
            };
        } else if (key === 'assetId') {
            const selectedAsset = availableAssets.find((a) => a.id === value);
            if (selectedAsset) {
                updated[itemIndex].assetId = value;
                updated[itemIndex].identifier = selectedAsset.tagNumber || selectedAsset.batchId || selectedAsset.assetName;
                updated[itemIndex].availableQuantity = selectedAsset.available_quantity || selectedAsset.quantity;
                updated[itemIndex].assetName = selectedAsset.assetName || selectedAsset.animalName;
                updated[itemIndex].breed = selectedAsset.breed;
                updated[itemIndex].weight = selectedAsset.weight || 0;
                updated[itemIndex].type_slug = selectedAsset.type_slug;
            }
        } else {
            updated[itemIndex][key] = value;
        }

        setItems(updated);
    };

    const renderItemFields = (item) => {
        const assetTypeConfig = getAssetTypeConfig(item.type_slug);
        const isEgg = isEggItem(item);

        // Get selected asset details
        const selectedAsset = availableAssets.find((a) => a.id === item.assetId);
        const currentWeight = selectedAsset?.current_weight || selectedAsset?.weight || 0;

        const isLivestock = item.type_slug && selectedAsset?.asset_category === 'livestock';
        const isSlaughtered = selectedAsset?.status === 'slaughtered'; // <-- new

        // Special handling for eggs
        if (isEgg) {
            return (
                <>
                    <Col xs={24} sm={3}>
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Egg Type *
                            </Text>
                        </div>
                        <Select
                            value={item.eggType}
                            onChange={(v) => updateItem(item.id, 'eggType', v)}
                            style={{ width: '100%' }}
                            size="middle"
                        >
                            <Select.Option value="normal">Normal Eggs</Select.Option>
                            <Select.Option value="medium">Medium Eggs</Select.Option>
                            <Select.Option value="double_yolk">Double Yolk</Select.Option>
                            <Select.Option value="broken">Broken Eggs</Select.Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={3}>
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Trays *
                            </Text>
                        </div>
                        <InputNumber
                            value={item.quantity}
                            onChange={(v) => updateItem(item.id, 'quantity', v)}
                            placeholder="Number of trays"
                            min={0}
                            style={{ width: '100%' }}
                            size="middle"
                        />
                    </Col>
                    <Col xs={24} sm={3}>
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Broken (Eggs)
                            </Text>
                        </div>
                        <InputNumber
                            value={item.brokenDuringSale}
                            onChange={(v) => updateItem(item.id, 'brokenDuringSale', v)}
                            placeholder="Broken eggs"
                            min={0}
                            style={{ width: '100%' }}
                            size="middle"
                        />
                    </Col>
                    <Col xs={24} sm={3}>
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Double Yolk (Eggs)
                            </Text>
                        </div>
                        <InputNumber
                            value={item.doubleYolkDuringSale}
                            onChange={(v) => updateItem(item.id, 'doubleYolkDuringSale', v)}
                            placeholder="Double yolk"
                            min={0}
                            style={{ width: '100%' }}
                            size="middle"
                        />
                    </Col>
                    <Col xs={24} sm={3}>
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Medium (Eggs)
                            </Text>
                        </div>
                        <InputNumber
                            value={item.mediumDuringSale}
                            onChange={(v) => updateItem(item.id, 'mediumDuringSale', v)}
                            placeholder="Medium eggs"
                            min={0}
                            style={{ width: '100%' }}
                            size="middle"
                        />
                    </Col>
                    <Col xs={24} sm={2}>
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Spoilt (Eggs)
                            </Text>
                        </div>
                        <InputNumber
                            value={item.spoiltDuringSale}
                            onChange={(v) => updateItem(item.id, 'spoiltDuringSale', v)}
                            placeholder="Spoilt eggs"
                            min={0}
                            style={{ width: '100%' }}
                            size="middle"
                        />
                    </Col>
                </>
            );
        }

        if (item.type_slug === 'waste') {
            const maxAvailable = item.weight || 0;
            const weightToSell = item.weightToSell ?? maxAvailable; // default to full available on first render

            return (
                <>
                    <Col xs={24} sm={3}>
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Biological Item
                            </Text>
                        </div>
                        <Input value={item.wasteName} disabled />
                    </Col>
                    <Col xs={24} sm={3}>
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Animal
                            </Text>
                        </div>
                        {item.tagNumber && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                From: {item.tagNumber}
                                {item.animalName ? ` (${item.animalName})` : ''}
                            </Text>
                        )}
                    </Col>
                    <Col xs={24} sm={3}>
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Available (kg)
                            </Text>
                        </div>
                        <InputNumber value={maxAvailable} disabled style={{ width: '100%' }} addonAfter="kg" />
                    </Col>
                    <Col xs={24} sm={3}>
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Sell (kg) *
                            </Text>
                        </div>
                        <InputNumber
                            value={weightToSell}
                            onChange={(v) => {
                                // Clamp so it can never exceed what's available, and never go negative
                                const clamped = Math.min(Math.max(v || 0, 0), maxAvailable);
                                updateItem(item.id, 'weightToSell', clamped);
                            }}
                            min={0.1}
                            max={maxAvailable}
                            step={0.1}
                            placeholder="kg to sell"
                            style={{ width: '100%' }}
                            addonAfter="kg"
                        />
                    </Col>
                    <Col xs={24} sm={3}>
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Price per kg *
                            </Text>
                        </div>
                        <InputNumber
                            value={item.pricePerUnit}
                            onChange={(v) => updateItem(item.id, 'pricePerUnit', v)}
                            placeholder="Price/kg"
                            min={0}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col xs={24} sm={2}>
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Subtotal
                            </Text>
                        </div>
                        <Text strong style={{ fontSize: 14 }}>
                            {formatKES((weightToSell || 0) * (item.pricePerUnit || 0))}
                        </Text>
                    </Col>
                    <Col xs={6} sm={1}>
                        <div style={{ marginBottom: 4, visibility: 'hidden' }}>Status</div>
                        {isCurrentItemComplete(item) ? (
                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                        ) : (
                            <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 18 }} />
                        )}
                    </Col>
                    <Col xs={6} sm={1}>
                        <div style={{ marginBottom: 4, visibility: 'hidden' }}>Action</div>
                        <Button danger icon={<DeleteOutlined />} onClick={() => removeItem(item.id)} size="small" />
                    </Col>
                </>
            );
        }

        // Regular asset handling
        const availableAssetsForType = getAssetsByType(item.type_slug);

        return (
            <>
                <Col xs={24} sm={5}>
                    <div style={{ marginBottom: 4 }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Select {assetTypeConfig?.name || 'Asset'} *
                        </Text>
                    </div>
                    {loadingAssets ? (
                        <Spin size="small" />
                    ) : (
                        <Select
                            value={item.assetId}
                            onChange={(v) => {
                                updateItem(item.id, 'assetId', v);
                                updateItem(item.id, 'weightToSell', null);

                                // Force weight-based, partial-only selling for slaughtered animals
                                const chosen = availableAssets.find((a) => a.id === v);
                                if (chosen?.status === 'slaughtered') {
                                    updateItem(item.id, 'pricingMethod', 'weight');
                                    updateItem(item.id, 'saleType', 'partial_weight');
                                }
                            }}
                            placeholder={`Select ${assetTypeConfig?.name || 'asset'}`}
                            style={{ width: '100%' }}
                            size="middle"
                            showSearch
                            optionFilterProp="children"
                            notFoundContent={`No ${assetTypeConfig?.name || 'assets'} available`}
                        >
                            {availableAssetsForType.map((asset) => (
                                <Select.Option key={asset.id} value={asset.id}>
                                    <Space direction="vertical" size={0} style={{ width: '100%' }}>
                                        <div>
                                            <Text>{asset.tagNumber || asset.batchId || asset.assetName || asset.animalName}</Text>
                                            {asset.breed && <span style={{ marginLeft: 8 }}>{asset.breed}</span>}
                                            {asset.status === 'slaughtered' ? (
                                                <Tag color="red" style={{ marginLeft: 8 }}>
                                                    Slaughtered · {asset.current_weight}kg sellable
                                                </Tag>
                                            ) : (
                                                <>
                                                    : Available: {asset.available_quantity || asset.quantity}
                                                    {asset.weight && ` | Weight: ${asset.weight}kg`}
                                                    {asset.current_weight && asset.current_weight !== asset.weight && (
                                                        <Tag color="orange" style={{ marginLeft: 8 }}>
                                                            Current: {asset.current_weight}kg
                                                        </Tag>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </Space>
                                </Select.Option>
                            ))}
                        </Select>
                    )}
                </Col>

                {/* Pricing Method Selection — hidden entirely once slaughtered, it's always weight */}
                <Col xs={24} sm={3}>
                    <div style={{ marginBottom: 4 }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Pricing Method *
                        </Text>
                    </div>
                    {isSlaughtered ? (
                        <Tag color="blue" style={{ padding: '4px 10px' }}>
                            By Weight (kg)
                        </Tag>
                    ) : (
                        <Select
                            value={item.pricingMethod}
                            onChange={(v) => {
                                updateItem(item.id, 'pricingMethod', v);
                                updateItem(item.id, 'weightToSell', null);
                            }}
                            style={{ width: '100%' }}
                            size="middle"
                        >
                            <Select.Option value="quantity">
                                <Space>
                                    <NumberOutlined /> By Quantity
                                </Space>
                            </Select.Option>
                            <Select.Option value="weight">
                                <Space>
                                    <FastForwardFilled /> By Weight (kg)
                                </Space>
                            </Select.Option>
                        </Select>
                    )}
                </Col>

                {/* For Weight-based Pricing */}
                {(item.pricingMethod === 'weight' || isSlaughtered) && (
                    <>
                        <Col xs={24} sm={3}>
                            <div style={{ marginBottom: 4 }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Sale Type *
                                </Text>
                            </div>
                            {isSlaughtered ? (
                                <Tag color="blue" style={{ padding: '4px 10px' }}>
                                    Sell by Weight (kg)
                                </Tag>
                            ) : (
                                <Select
                                    value={item.saleType || 'partial_weight'}
                                    onChange={(v) => {
                                        updateItem(item.id, 'saleType', v);
                                        updateItem(item.id, 'weightToSell', null);
                                    }}
                                    style={{ width: '100%' }}
                                    size="middle"
                                >
                                    <Option value="partial_weight">Sell by Weight (kg)</Option>
                                    {isLivestock && <Option value="full_weight">Sell Full Animal (by kg rate)</Option>}
                                </Select>
                            )}
                        </Col>

                        {/* partial_weight fields — unchanged, currentWeight already caps at
                        selectedAsset.current_weight, which is now the sellable meat weight */}
                        <Col xs={24} sm={3}>
                            <div style={{ marginBottom: 4 }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {isSlaughtered ? 'Remaining Sellable (kg)' : 'Current Weight (kg)'}
                                </Text>
                            </div>
                            <InputNumber value={currentWeight} disabled style={{ width: '100%' }} size="middle" addonAfter="kg" />
                        </Col>
                        <Col xs={24} sm={3}>
                            <div style={{ marginBottom: 4 }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Weight to Sell (kg) *
                                </Text>
                            </div>
                            <InputNumber
                                value={item.weightToSell}
                                onChange={(v) => updateItem(item.id, 'weightToSell', v)}
                                placeholder="kg to sell"
                                min={0.1}
                                max={currentWeight}
                                step={0.1}
                                style={{ width: '100%' }}
                                size="middle"
                                addonAfter="kg"
                            />
                        </Col>
                        <Col xs={24} sm={2}>
                            <div style={{ marginBottom: 4 }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Remaining After Sale
                                </Text>
                            </div>
                            <Text strong style={{ color: '#fa8c16', fontSize: 14 }}>
                                {Math.max(0, currentWeight - (item.weightToSell || 0)).toFixed(2)} kg
                            </Text>
                        </Col>
                    </>
                )}

                {/* For Quantity-based Pricing - No sale type, just quantity */}
                {item.pricingMethod === 'quantity' && (
                    <>
                        <Col xs={24} sm={3}>
                            <div style={{ marginBottom: 4 }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Quantity *
                                </Text>
                            </div>
                            <InputNumber
                                value={item.quantity}
                                onChange={(v) => updateItem(item.id, 'quantity', v)}
                                placeholder="Number of animals"
                                min={1}
                                max={item.availableQuantity}
                                style={{ width: '100%' }}
                                size="middle"
                            />
                        </Col>
                        {isLivestock && currentWeight > 0 && (
                            <Col xs={24} sm={2}>
                                <div style={{ marginBottom: 4 }}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        Est. Total Weight
                                    </Text>
                                </div>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {((item.quantity || 1) * currentWeight).toFixed(2)} kg
                                </Text>
                            </Col>
                        )}
                    </>
                )}

                {/* Subtotal preview */}
                {(item.pricingMethod === 'weight' || isSlaughtered) && item.weightToSell > 0 && (
                    <Col xs={24} sm={2}>
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Subtotal
                            </Text>
                        </div>
                        <Text strong style={{ color: '#1677ff', fontSize: 14 }}>
                            {formatKES((item.weightToSell || 0) * (item.pricePerUnit || 0))}
                        </Text>
                    </Col>
                )}
            </>
        );
    };

    const getPriceLabel = (item) => {
        if (isEggItem(item)) return 'Price per tray *';
        if (item.type_slug === 'waste') return 'Price per kg *';
        if (item.pricingMethod === 'weight') return 'Price per kg *';
        return 'Price per unit *';
    };

    const handleSubmitSale = async () => {
        try {
            setIsSubmitting(true);
            const formData = form.getFieldsValue();

            if (!areAllItemsComplete() || !formData.stakeholderId || !formData.saleDate || totalAmount <= 0) {
                message.error('Please complete all items before submitting.');
                return;
            }

            const creditToApply = form.getFieldValue('credit_to_apply') || 0;
            const finalTotal = Math.max(0, totalAmount - creditToApply);

            const saleData = {
                stakeholderId: formData.stakeholderId,
                paymentMethod: formData.paymentMethod || 'unpaid',
                totalAmount: totalAmount || finalTotal,
                originalTotal: totalAmount,
                creditApplied: creditToApply,
                notes: formData.notes,
                saleType: 'unified',
                farmId: formData.farmId,
                servedBy: account?.firstname || 'System',
                recorded: dayjs(formData.saleDate).format('YYYY-MM-DD'),
                paymentStatus: 'unpaid',
                delivery: formData.deliveredMethod,
                delGuy: formData.deliveryPersonId,
                userId: account?.id,
                userName: account?.firstname,
                items: items.map((item) => {
                    if (item.type_slug === 'waste') {
                        return {
                            itemType: 'waste',
                            category: 'waste',
                            wasteName: item.wasteName,
                            weight: item.weight, // available, for audit
                            weightToSell: item.weightToSell, // <-- what's actually being sold
                            unitPrice: item.pricePerUnit,
                            totalPrice: calculateSubtotal(item),
                            slaughterId: item.slaughterId,
                            animalId: item.animalId,
                            tagNumber: item.tagNumber,
                            animalName: item.animalName,
                            farm: formData.farmId,
                            pricingMethod: 'weight',
                            quantity: 1
                        };
                    }
                    if (isEggItem(item)) {
                        return {
                            itemType: 'egg',
                            type_slug: 'egg',
                            batchId: item.batchId,
                            eggType: item.eggType,
                            quantity: item.quantity,
                            traysCount: item.quantity || 0,
                            brokenEggsCount: item.brokenDuringSale || 0,
                            doubleYolkCount: item.doubleYolkDuringSale || 0,
                            mediumEggsCount: item.mediumDuringSale || 0,
                            spoiltEggsCount: item.spoiltDuringSale || 0,
                            unitPrice: item.pricePerUnit,
                            totalPrice: calculateSubtotal(item),
                            pricingMethod: 'quantity'
                        };
                    } else {
                        const asset = availableAssets.find((a) => a.id === item.assetId);
                        const baseItem = {
                            itemType: 'asset',
                            assetId: item.assetId,
                            type_slug: item.type_slug,
                            assetName: asset?.assetName || asset?.animalName,
                            identifier: asset?.tagNumber || asset?.batchId,
                            unitPrice: item.pricePerUnit,
                            totalPrice: calculateSubtotal(item),
                            pricingMethod: item.pricingMethod
                        };

                        if (item.pricingMethod === 'weight') {
                            baseItem.saleType = item.saleType || 'partial_weight';
                            if (item.saleType === 'partial_weight') {
                                baseItem.weightToSell = item.weightToSell;
                                baseItem.currentWeight = asset?.current_weight || asset?.weight;
                                baseItem.quantity = 0;
                            } else if (item.saleType === 'full_weight') {
                                baseItem.quantity = item.quantity || 1;
                                baseItem.weightPerAnimal = asset?.current_weight || asset?.weight;
                            }
                        } else {
                            baseItem.quantity = item.quantity || 0;
                            baseItem.weightKg = item.pricingMethod === 'weight' ? item.weight : null;
                        }

                        return baseItem;
                    }
                })
            };
            const fd = new FormData();

            formData?.images?.fileList?.forEach((file) => {
                fd.append('images', file?.originFileObj);
            });

            fd.append('saleData', JSON.stringify(saleData));
            const response = await api.post(`/unified`, fd, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                message.success('Sale recorded successfully!');
                setItems([]);
                form.resetFields();
                setUseCredit(0);
                fetchAvailableAssets();
                fetchEggInventory();
                fetchCustomerCredit(formData.stakeholderId);

                setReceiptData({
                    receiptId: response.data.receiptId,
                    transactionId: response.data.transactionId,
                    saleId: response.data.saleId,
                    saleData: formData,
                    items: items,
                    totalAmount: finalTotal,
                    originalTotal: totalAmount,
                    creditApplied: creditToApply,
                    timestamp: new Date()
                });
                setShowSuccessReceipt(true);

                history.push('/sales');
            }
        } catch (error) {
            console.error('Error submitting sale:', error);
            message.error(error.response?.data?.message || 'Failed to record sale');
        } finally {
            setIsSubmitting(false);
        }
    };

    const groupedAssetTypes = assetTypes.reduce((groups, type) => {
        const category = type.category === 'livestock' ? '🐔 Livestock' : '🔧 Physical Assets';
        if (!groups[category]) groups[category] = [];
        groups[category].push(type);
        return groups;
    }, {});

    const eggsType = { slug: 'eggs', name: 'Eggs', color: 'gold', prefix: 'EGG', category: 'livestock' };
    if (!groupedAssetTypes['🐔 Livestock']) groupedAssetTypes['🐔 Livestock'] = [];
    if (!groupedAssetTypes['🐔 Livestock'].find((t) => t.slug === 'eggs')) {
        groupedAssetTypes['🐔 Livestock'].push(eggsType);
    }

    const totalAmount = items.reduce((sum, item) => sum + calculateSubtotal(item), 0);
    const creditToApply = form.getFieldValue('credit_to_apply') || 0;
    const finalAmount = Math.max(0, totalAmount - creditToApply);

    const handleFormChange = () => {
        const formValues = form.getFieldsValue();
        onChange?.({
            items,
            totalAmount: finalAmount,
            originalTotal: totalAmount,
            creditApplied: creditToApply,
            ...formValues
        });
    };

    useEffect(() => {
        handleFormChange();
    }, [items, deliveredMethod, creditToApply]);

    const creditBalance = customerCredit;
    const currentCustomerId = form.getFieldValue('stakeholderId');
    const currentCustomer = stakeholders.find((s) => s.id === currentCustomerId);

    return (
        <>
            <Form
                form={form}
                layout="vertical"
                onValuesChange={handleFormChange}
                initialValues={{
                    saleDate: dayjs(),
                    deliveredMethod: 'picked',
                    paymentMethod: 'cash',
                    credit_to_apply: 0
                }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="stakeholderId" label="Customer" rules={[{ required: true }]}>
                            <Select
                                placeholder="Select or add customer"
                                showSearch
                                optionFilterProp="children"
                                onChange={handleCustomerChange}
                                dropdownRender={(menu) => (
                                    <>
                                        {menu}
                                        <Divider style={{ margin: '8px 0' }} />
                                        <Button
                                            type="link"
                                            icon={<UserAddOutlined />}
                                            onClick={() => setCustomerModalVisible(true)}
                                            style={{ width: '100%', textAlign: 'center' }}
                                        >
                                            Add New Customer
                                        </Button>
                                    </>
                                )}
                            >
                                {stakeholders.map((s) => (
                                    <Select.Option key={s.id} value={s.id}>
                                        {s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim()}
                                        {s.phone && <span style={{ fontSize: 12, color: '#999', marginLeft: 8 }}>({s.phone})</span>}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
    <Form.Item
      name="saleDate"
      label="Sale Date"
      rules={[{ required: true, message: "Please select a sale date" }]}
    >
      <DatePicker
        className="w-full"
        format="YYYY-MM-DD"
        style={{ width: "100%" }}
      />
    </Form.Item>
  </Col>
                </Row>

                <Space direction="horizontal" style={{ width: '100%' }} wrap>
                    <Form.Item name="deliveredMethod" label="Delivery Method" rules={[{ required: true }]}>
                        <Select onChange={(value) => setDeliveredMethod(value)} style={{ width: 150 }}>
                            <Select.Option value="delivered">Delivered</Select.Option>
                            <Select.Option value="picked">Picked at Farm</Select.Option>
                        </Select>
                    </Form.Item>

                    {deliveredMethod === 'delivered' && (
                        <Form.Item name="deliveryPersonId" label="Delivery Person" rules={[{ required: true }]}>
                            <Select placeholder="Select delivery person" showSearch style={{ width: 180 }}>
                                {employees.map((e) => (
                                    <Select.Option key={e.id} value={e.id}>
                                        {e.firstname} {e.lastname}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item name="farmId" label="Farm" rules={[{ required: true }]}>
                        <Select placeholder="Select farm" showSearch style={{ width: 150 }}>
                            {farms.map((s) => (
                                <Select.Option key={s.id} value={s.id}>
                                    {s.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="paymentMethod" label="Payment Method" rules={[{ required: true }]}>
                        <Select style={{ width: 120 }}>
                            <Select.Option value="cash">Cash</Select.Option>
                            <Select.Option value="unpaid">Credit</Select.Option>
                        </Select>
                    </Form.Item>

                    {currentCustomerId && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                background: '#f6ffed',
                                padding: '4px 12px',
                                borderRadius: 6
                            }}
                        >
                            <WalletOutlined style={{ color: '#52c41a' }} />
                            <span>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Credit Balance:
                                </Text>
                                <Text strong style={{ color: '#52c41a', marginLeft: 4 }}>
                                    {formatKES(creditBalance)}
                                </Text>
                            </span>
                            {creditBalance > 0 && (
                                <Tooltip title="Apply credit to this sale">
                                    <Button
                                        size="small"
                                        type="primary"
                                        ghost
                                        icon={<CreditCardOutlined />}
                                        onClick={() => setShowCreditModal(true)}
                                    >
                                        Apply Credit
                                    </Button>
                                </Tooltip>
                            )}
                            <Tooltip title="Add prepayment/credit to customer account">
                                <Button size="small" icon={<PlusOutlined />} onClick={() => setAddCreditModalVisible(true)}>
                                    Add Credit
                                </Button>
                            </Tooltip>
                            <Tooltip title="View credit history">
                                <Button size="small" icon={<HistoryOutlined />} onClick={() => fetchCreditHistory(currentCustomerId)}>
                                    History
                                </Button>
                            </Tooltip>
                        </div>
                    )}
                </Space>

                <Divider />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Title level={4} style={{ margin: 0 }}>
                        Sale Items
                    </Title>
                    <Button icon={<ReloadOutlined />} onClick={fetchAvailableAssets} loading={loadingAssets} size="small">
                        Refresh Assets
                    </Button>
                </div>

                {items.length > 0 && !areAllItemsComplete() && (
                    <Alert
                        message="Incomplete Items"
                        description="Please complete all fields in the current item before adding another."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                {items.map((item) => (
                    <Card
                        key={item.id}
                        style={{
                            border: isCurrentItemComplete(item) ? '2px solid #52c41a' : '1px solid #d9d9d9',
                            marginBottom: 16,
                            borderRadius: 8,
                            backgroundColor: isCurrentItemComplete(item) ? '#f6ffed' : 'white'
                        }}
                        size="small"
                    >
                        <Row gutter={8} align="middle">
                            <Col xs={24} sm={3}>
                                <div style={{ marginBottom: 4 }}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        Asset Type *
                                    </Text>
                                </div>
                                {item.type_slug === 'waste' ? (
                                    <Tag color="volcano" style={{ padding: '4px 10px', fontSize: 12 }}>
                                        Slaughter Waste
                                    </Tag>
                                ) : (
                                    <Select
                                        value={item.type_slug}
                                        onChange={(v) => updateItem(item.id, 'type_slug', v)}
                                        placeholder="Select asset type"
                                        style={{ width: '100%' }}
                                        size="middle"
                                        showSearch
                                    >
                                        {Object.entries(groupedAssetTypes).map(([category, types]) => (
                                            <Select.OptGroup key={category} label={category}>
                                                {types.map((type) => (
                                                    <Select.Option key={type.slug} value={type.slug}>
                                                        <Space>
                                                            <span>{type.name}</span>
                                                            <Badge
                                                                count={type.available_count || 0}
                                                                style={{ backgroundColor: '#52c41a' }}
                                                            />
                                                        </Space>
                                                    </Select.Option>
                                                ))}
                                            </Select.OptGroup>
                                        ))}
                                    </Select>
                                )}
                            </Col>

                            {item.type_slug && renderItemFields(item)}

                            {item.type_slug !== 'waste' && (
                                <>
                                    <Col xs={24} sm={3}>
                                        <div style={{ marginBottom: 4 }}>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {getPriceLabel(item)}
                                            </Text>
                                        </div>
                                        <InputNumber
                                            value={item.pricePerUnit}
                                            onChange={(v) => updateItem(item.id, 'pricePerUnit', v)}
                                            placeholder="Price"
                                            min={0}
                                            prefix="KES"
                                            style={{ width: '100%' }}
                                            size="middle"
                                        />
                                    </Col>

                                    <Col xs={12} sm={2}>
                                        <div style={{ marginBottom: 4 }}>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                Subtotal
                                            </Text>
                                        </div>
                                        <Text strong style={{ fontSize: 14 }}>
                                            {formatKES(calculateSubtotal(item))}
                                        </Text>
                                    </Col>

                                    <Col xs={6} sm={1}>
                                        <div style={{ marginBottom: 4, visibility: 'hidden' }}>Status</div>
                                        {isCurrentItemComplete(item) ? (
                                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                                        ) : (
                                            <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 18 }} />
                                        )}
                                    </Col>

                                    <Col xs={6} sm={1}>
                                        <div style={{ marginBottom: 4, visibility: 'hidden' }}>Action</div>
                                        <Button danger icon={<DeleteOutlined />} onClick={() => removeItem(item.id)} size="small" />
                                    </Col>
                                </>
                            )}
                        </Row>
                    </Card>
                ))}

                <Space style={{ width: '100%', marginBottom: 16 }}>
                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={addItem}
                        style={{ width: '100%' }}
                        disabled={isAddButtonDisabled()}
                    >
                        Add Sale Item {isAddButtonDisabled() && '(Complete current item first)'}
                    </Button>
                </Space>

                <Button
                    type="dashed"
                    icon={<ShoppingOutlined />}
                    onClick={() => fetchUnrecordedWaste(1, wasteSearch)}
                    style={{ width: '100%', marginBottom: 16 }}
                >
                    Add Biological Items
                </Button>

                {items.length > 0 && (
                    <div style={{ textAlign: 'right', padding: '16px', background: '#f5f5f5', borderRadius: 8 }}>
                        <div style={{ marginBottom: 8 }}>
                            <Text>Subtotal:</Text>
                            <Text strong style={{ marginLeft: 16, fontSize: 16 }}>
                                {formatKES(totalAmount)}
                            </Text>
                        </div>
                        {creditToApply > 0 && (
                            <div style={{ marginBottom: 8, color: '#52c41a' }}>
                                <Text>Credit Applied:</Text>
                                <Text strong style={{ marginLeft: 16, fontSize: 16, color: '#52c41a' }}>
                                    - {formatKES(creditToApply)}
                                </Text>
                            </div>
                        )}
                        <Divider style={{ margin: '8px 0' }} />
                        <div>
                            <Title level={4} style={{ margin: 0, color: creditToApply >= totalAmount ? '#52c41a' : '#1677ff' }}>
                                Total Due: {formatKES(finalAmount)}
                            </Title>
                            {creditToApply >= totalAmount && (
                                <Tag color="green" style={{ marginTop: 8 }}>
                                    Fully paid by credit
                                </Tag>
                            )}
                        </div>
                    </div>
                )}

                <Divider />

                <Form.Item name="notes" label="Additional Notes">
                    <Input.TextArea placeholder="Any additional notes about this sale..." rows={3} />
                </Form.Item>

                <Form.Item name="images" label="Upload Images">
                    <Upload listType="picture" beforeUpload={() => false} multiple>
                        <Button icon={<UploadOutlined />}>Upload Images</Button>
                    </Upload>
                </Form.Item>

                <Divider />

                <Space>
                    <Button
                        type="primary"
                        icon={<PrinterOutlined />}
                        onClick={() => setPreviewVisible(true)}
                        disabled={!areAllItemsComplete()}
                    >
                        Preview Receipt
                    </Button>
                </Space>
            </Form>

            {/* Add Customer Modal */}
            <Modal
                title="Add New Customer"
                open={customerModalVisible}
                onCancel={() => {
                    setCustomerModalVisible(false);
                    customerForm.resetFields();
                }}
                footer={null}
                width={500}
            >
                <Form form={customerForm} layout="vertical" onFinish={handleAddCustomer}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="first_name" label="First Name">
                                <Input placeholder="Enter first name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="last_name" label="Last Name">
                                <Input placeholder="Enter last name" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="company_name" label="Business Name">
                        <Input placeholder="Enter business name" />
                    </Form.Item>

                    <Form.Item name="phone" label="Phone Number" rules={[{ required: true }]}>
                        <Input placeholder="Enter phone number" />
                    </Form.Item>

                    <Form.Item name="email" label="Email">
                        <Input placeholder="Enter email address" type="email" />
                    </Form.Item>

                    <Form.Item name="location" label="Location">
                        <Input placeholder="Enter location" />
                    </Form.Item>

                    <Form.Item name="address" label="Address">
                        <Input.TextArea rows={2} placeholder="Enter full address" />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={addingCustomer}>
                                Add Customer
                            </Button>
                            <Button
                                onClick={() => {
                                    setCustomerModalVisible(false);
                                    customerForm.resetFields();
                                }}
                            >
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>

                    <Alert
                        message="Note"
                        description="You can update additional customer details later from the Customers section."
                        type="info"
                        showIcon
                    />
                </Form>
            </Modal>

            {/* Apply Credit Modal */}
            <Modal
                title="Apply Customer Credit"
                open={showCreditModal}
                onCancel={() => {
                    setShowCreditModal(false);
                    setUseCredit(0);
                }}
                onOk={applyCredit}
                confirmLoading={creditLoading}
                width={450}
            >
                <div style={{ marginBottom: 16 }}>
                    <Alert
                        message="Available Credit"
                        description={`${formatKES(customerCredit)} available for ${currentCustomer?.name || 'this customer'}`}
                        type="info"
                        showIcon
                    />
                </div>
                <div style={{ marginBottom: 8 }}>
                    <Text strong>Sale Total: {formatKES(totalAmount)}</Text>
                </div>
                <Form.Item label="Amount to Apply">
                    <InputNumber
                        min={0}
                        max={Math.min(customerCredit, totalAmount)}
                        value={useCredit}
                        onChange={setUseCredit}
                        style={{ width: '100%' }}
                        prefix="KES"
                        size="large"
                    />
                </Form.Item>
                {useCredit > 0 && useCredit > totalAmount && (
                    <Alert
                        message="Overpayment Warning"
                        description={`You are applying KES ${useCredit.toLocaleString()} but the sale total is KES ${totalAmount.toLocaleString()}. The excess will be kept as customer credit.`}
                        type="warning"
                        showIcon
                        style={{ marginTop: 8 }}
                    />
                )}
                <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                    <Row>
                        <Col span={12}>
                            <Text>Sale Total:</Text>
                        </Col>
                        <Col span={12} style={{ textAlign: 'right' }}>
                            <Text strong>{formatKES(totalAmount)}</Text>
                        </Col>
                    </Row>
                    {useCredit > 0 && (
                        <>
                            <Row style={{ marginTop: 8 }}>
                                <Col span={12}>
                                    <Text>Credit to Apply:</Text>
                                </Col>
                                <Col span={12} style={{ textAlign: 'right' }}>
                                    <Text strong style={{ color: '#52c41a' }}>
                                        - {formatKES(useCredit)}
                                    </Text>
                                </Col>
                            </Row>
                            <Divider style={{ margin: '8px 0' }} />
                            <Row>
                                <Col span={12}>
                                    <Text strong>Amount Due After Credit:</Text>
                                </Col>
                                <Col span={12} style={{ textAlign: 'right' }}>
                                    <Text strong style={{ fontSize: 16, color: useCredit >= totalAmount ? '#52c41a' : '#1677ff' }}>
                                        {formatKES(Math.max(0, totalAmount - useCredit))}
                                    </Text>
                                </Col>
                            </Row>
                        </>
                    )}
                </div>
            </Modal>

            {/* Add Credit Modal */}
            <Modal
                title="Add Customer Credit / Prepayment"
                open={addCreditModalVisible}
                onCancel={() => {
                    setAddCreditModalVisible(false);
                    addCreditForm.resetFields();
                }}
                footer={null}
                width={500}
            >
                <Alert
                    message="Add Prepayment"
                    description="Add credit to customer account for future purchases"
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Form form={addCreditForm} layout="vertical" onFinish={handleAddCredit}>
                    <Form.Item name="amount" label="Amount (KES)" rules={[{ required: true }]}>
                        <InputNumber min={0} step={100} style={{ width: '100%' }} placeholder="Enter amount" size="large" />
                    </Form.Item>
                    <Form.Item name="payment_method" label="Payment Method" rules={[{ required: true }]}>
                        <Select placeholder="Select payment method">
                            <Option value="cash">Cash</Option>
                            <Option value="mpesa">M-Pesa</Option>
                            <Option value="bank_transfer">Bank Transfer</Option>
                            <Option value="cheque">Cheque</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="payment_date" label="Payment Date" initialValue={dayjs()} rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                    </Form.Item>
                    <Form.Item
                        name="debitAccount"
                        label="Debit Account"
                        rules={[
                            {
                                required: true,
                                message: 'Please select debit account'
                            }
                        ]}
                    >
                        <Select options={paidOptions} placeholder="Select debit account" showSearch optionFilterProp="label" />
                    </Form.Item>
                    <Form.Item name="expiry_days" label="Credit Expiry (Days)" tooltip="Leave empty for no expiry">
                        <InputNumber min={1} style={{ width: '100%' }} placeholder="e.g., 365" />
                    </Form.Item>
                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={2} placeholder="Reason for credit, reference number, etc." />
                    </Form.Item>
                    <Form.Item>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => setAddCreditModalVisible(false)}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={creditLoading}>
                                Add Credit
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Credit History Modal */}
            <Modal
                title="Customer Credit History"
                open={showCreditHistory}
                onCancel={() => setShowCreditHistory(false)}
                footer={null}
                width={800}
            >
                <Table
                    dataSource={creditHistory}
                    rowKey="id"
                    size="small"
                    columns={[
                        { title: 'Date', dataIndex: 'payment_date', render: (d) => dayjs(d).format('DD MMM YYYY'), width: 110 },
                        { title: 'Type', dataIndex: 'transaction_type', render: (t) => <Tag color="blue">{t}</Tag>, width: 100 },
                        { title: 'Amount', dataIndex: 'amount', render: (a) => formatKES(a), width: 100 },
                        { title: 'Used', dataIndex: 'used_amount', render: (u) => formatKES(u), width: 100 },
                        {
                            title: 'Remaining',
                            dataIndex: 'remaining_amount',
                            render: (r) => <Tag color={r > 0 ? 'green' : 'default'}>{formatKES(r)}</Tag>,
                            width: 100
                        },
                        {
                            title: 'Status',
                            dataIndex: 'status',
                            render: (s) => <Tag color={s === 'active' ? 'green' : 'red'}>{s}</Tag>,
                            width: 80
                        },
                        { title: 'Notes', dataIndex: 'notes', ellipsis: true }
                    ]}
                    pagination={{ pageSize: 5 }}
                />
            </Modal>

            {/* Preview Modal */}
            <Modal
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                title="Sale Receipt Preview"
                width={800}
                footer={[
                    <Button key="cancel" onClick={() => setPreviewVisible(false)}>
                        Close
                    </Button>,
                    <Button key="submit" type="primary" loading={isSubmitting} onClick={handleSubmitSale}>
                        Confirm & Submit Sale
                    </Button>
                ]}
            >
                <ReceiptPreview
                    formData={form.getFieldsValue()}
                    items={items}
                    totalAmount={finalAmount}
                    originalTotal={totalAmount}
                    creditApplied={creditToApply}
                    calculateSubtotal={calculateSubtotal}
                    isEggItem={isEggItem}
                    isPreview={true}
                    stakeholders={stakeholders}
                    availableAssets={availableAssets}
                    eggInventoryData={eggInventoryData}
                />
            </Modal>

            {/* Success Modal */}
            <Modal
                open={showSuccessReceipt}
                onCancel={() => setShowSuccessReceipt(false)}
                title="Sale Completed Successfully!"
                width={800}
                footer={[
                    <Button key="close" onClick={() => setShowSuccessReceipt(false)}>
                        Close
                    </Button>,
                    <Button
                        key="download"
                        type="primary"
                        onClick={() => {
                            message.success('Receipt downloaded');
                        }}
                    >
                        Download Receipt
                    </Button>
                ]}
            >
                {receiptData && (
                    <ReceiptPreview
                        formData={receiptData.saleData}
                        items={receiptData.items}
                        totalAmount={receiptData.totalAmount}
                        originalTotal={receiptData.originalTotal}
                        creditApplied={receiptData.creditApplied}
                        calculateSubtotal={calculateSubtotal}
                        isEggItem={isEggItem}
                        receiptId={receiptData.receiptId}
                        transactionId={receiptData.transactionId}
                        isPreview={false}
                        stakeholders={stakeholders}
                        availableAssets={availableAssets}
                        eggInventoryData={eggInventoryData}
                    />
                )}
            </Modal>

            {/* Biological Item Picker Modal */}
            <Modal
                title="Select Biological Items from Slaughter"
                open={wasteModalVisible}
                onCancel={() => {
                    setWasteModalVisible(false);
                    setSelectedWasteRows([]);
                    setWasteSearch('');
                }}
                width={800}
                footer={[
                    <Button key="cancel" onClick={() => setWasteModalVisible(false)}>
                        Cancel
                    </Button>,
                    <Button key="add" type="primary" onClick={addSelectedWasteToSale} disabled={selectedWasteRows.length === 0}>
                        Add Selected ({selectedWasteRows.length})
                    </Button>
                ]}
            >
                <Input.Search
                    placeholder="Search by tag number or animal name"
                    value={wasteSearch}
                    onChange={(e) => setWasteSearch(e.target.value)}
                    onSearch={(value) => fetchUnrecordedWaste(1, value)}
                    style={{ marginBottom: 16 }}
                    enterButton
                    allowClear
                />
                <Table
                    rowSelection={{
                        type: 'checkbox',
                        selectedRowKeys: selectedWasteRows.map((r) => wasteRowKey(r.slaughterId, r.name)),
                        onChange: (selectedRowKeys, selectedRows) => setSelectedWasteRows(selectedRows)
                    }}
                    columns={[
                        { title: 'Item', dataIndex: 'name' },
                        { title: 'Weight (kg)', dataIndex: 'weight', render: (w) => `${w} kg` },
                        { title: 'From Animal', dataIndex: 'tagNumber' },
                        { title: 'Animal Name', dataIndex: 'animalName' },
                        { title: 'Slaughter Date', dataIndex: 'slaughterDate', render: (d) => dayjs(d).format('DD/MM/YYYY') }
                    ]}
                    dataSource={availableWaste}
                    rowKey={(record) => wasteRowKey(record.slaughterId, record.name)}
                    pagination={{
                        current: wastePagination.page,
                        pageSize: wastePagination.limit,
                        total: wastePagination.total,
                        onChange: (page) => fetchUnrecordedWaste(page, wasteSearch),
                        showSizeChanger: false
                    }}
                    loading={loadingWaste}
                    locale={{ emptyText: 'No unrecorded biological items available' }}
                />
            </Modal>
        </>
    );
}

function ReceiptPreview({
    formData,
    items,
    totalAmount,
    calculateSubtotal,
    isEggItem,
    receiptId,
    transactionId,
    isPreview = true,
    stakeholders = [],
    availableAssets = [],
    eggInventoryData = []
}) {
    const { profile } = useFarmProfile();

    const getItemDescription = (item) => {
        if (isEggItem(item)) {
            const eggTypeLabels = { normal: 'Normal', medium: 'Medium', double_yolk: 'Double Yolk', broken: 'Broken' };
            const label = eggTypeLabels[item.eggType] || 'Eggs';
            return {
                name: `${label} Eggs`,
                meta: `${item.quantity} trays @ ${formatKES(item.pricePerUnit)}/tray`
            };
        }

        if (item.isWaste || item.type_slug === 'waste') {
            return {
                name: item.wasteName || 'Waste Product',
                meta: `${item.tagNumber ? item.tagNumber + ' · ' : ''}${item.weightToSell} kg @ ${formatKES(item.pricePerUnit)}/kg`
            };
        }

        const asset = availableAssets.find((a) => a.id === item.assetId);
        if (!asset) return { name: 'Product', meta: '' };

        const typeName = asset.type_name || asset.type_slug;
        const identifier = asset.tagNumber || asset.batchId || asset.assetName || asset.animalName;

        if (item.saleType === 'partial_weight' && item.weightToSell) {
            return {
                name: typeName,
                meta: `${identifier} · ${item.weightToSell} kg @ ${formatKES(item.pricePerUnit)}/kg`
            };
        }

        if (item.saleType === 'full_weight' && item.weightPerAnimal) {
            const totalWeight = (item.quantity || 1) * item.weightPerAnimal;
            return {
                name: typeName,
                meta: `${identifier} · ${item.quantity || 1} animal(s) × ${item.weightPerAnimal}kg = ${totalWeight}kg @ ${formatKES(
                    item.pricePerUnit
                )}/kg`
            };
        }

        if (item.pricingMethod === 'weight' && item.weight) {
            const totalWeight = (item.weight || 1) * (item.quantity || 1);
            return {
                name: typeName,
                meta: `${identifier} · ${item.quantity || 1} unit(s) @ ${item.weight}kg each = ${totalWeight}kg @ ${formatKES(
                    item.pricePerUnit
                )}/kg`
            };
        }

        return {
            name: typeName,
            meta: `${identifier} · ${item.quantity || 1} unit(s) @ ${formatKES(item.pricePerUnit)}/unit`
        };
    };

    const customerName =
        stakeholders.find((s) => s.id === formData?.stakeholderId)?.name ||
        stakeholders.find((s) => s.id === formData?.stakeholderId)?.first_name ||
        `Customer #${formData?.stakeholderId}`;

    const receiptNum = receiptId ? String(receiptId).padStart(6, '0') : 'PREVIEW';
    const isPaid = formData?.paymentStatus === 'fully_paid';
    const isPartial = formData?.paymentStatus === 'partial';

    const styles = {
        wrapper: {
            fontFamily: "'Courier New', Courier, monospace",
            maxWidth: 420,
            margin: '0 auto',
            background: '#fff',
            border: '1px solid #e5e5e5',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
        },
        header: {
            background: '#1a1a1a',
            padding: '24px 28px 20px',
            textAlign: 'center'
        },
        logo: {
            fontSize: 22,
            fontWeight: 900,
            letterSpacing: 8,
            color: '#fff',
            textTransform: 'uppercase',
            margin: 0
        },
        tagline: {
            fontSize: 10,
            color: '#888',
            letterSpacing: 3,
            textTransform: 'uppercase',
            marginTop: 4
        },
        badge: {
            display: 'inline-block',
            marginTop: 14,
            padding: '4px 14px',
            fontSize: 9,
            letterSpacing: 3,
            textTransform: 'uppercase',
            border: `1px solid ${isPreview ? '#555' : '#279e6c'}`,
            color: isPreview ? '#888' : '#279e6c',
            borderRadius: 1
        },
        body: { padding: '0 28px 28px' },
        amountSection: {
            borderBottom: '2px dashed #e5e5e5',
            padding: '24px 0 20px',
            textAlign: 'center'
        },
        amountLabel: {
            fontSize: 10,
            color: '#999',
            letterSpacing: 2,
            textTransform: 'uppercase'
        },
        amountValue: {
            fontSize: 36,
            fontWeight: 900,
            color: '#1a1a1a',
            lineHeight: 1.2,
            marginTop: 4,
            letterSpacing: 1
        },
        statusBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            marginTop: 8,
            padding: '3px 12px',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 2,
            textTransform: 'uppercase',
            borderRadius: 20,
            background: isPaid ? '#e6f7f0' : isPartial ? '#fff8e1' : '#fce4ec',
            color: isPaid ? '#0f6e56' : isPartial ? '#b45309' : '#c62828'
        },
        section: { padding: '18px 0', borderBottom: '1px dashed #e5e5e5' },
        sectionTitle: {
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: 3,
            textTransform: 'uppercase',
            color: '#aaa',
            marginBottom: 10
        },
        row: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            padding: '5px 0',
            fontSize: 12,
            gap: 12
        },
        rowLabel: { color: '#888', flexShrink: 0 },
        rowValue: { color: '#1a1a1a', fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' },
        itemRow: {
            padding: '8px 0',
            borderBottom: '1px dotted #eee',
            fontSize: 12
        },
        itemName: { fontWeight: 700, color: '#1a1a1a', marginBottom: 2 },
        itemMeta: { fontSize: 10, color: '#999', letterSpacing: 0.3 },
        itemSubtotal: { fontWeight: 700, color: '#1a1a1a', textAlign: 'right', marginTop: 4, fontSize: 13 },
        totalRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '18px 0 0',
            borderTop: '2px solid #1a1a1a',
            marginTop: 8
        },
        totalLabel: { fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#1a1a1a' },
        totalValue: { fontSize: 20, fontWeight: 900, color: '#1a1a1a' },
        footer: {
            borderTop: '2px dashed #e5e5e5',
            padding: '20px 0 0',
            textAlign: 'center'
        },
        thankYou: { fontSize: 13, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: '#1a1a1a' },
        footerMeta: { fontSize: 10, color: '#aaa', marginTop: 6, letterSpacing: 0.5 },
        receiptNum: {
            background: '#f5f5f5',
            padding: '10px 28px',
            textAlign: 'center',
            fontSize: 10,
            color: '#bbb',
            letterSpacing: 4,
            fontFamily: "'Courier New', Courier, monospace"
        }
    };

    return (
        <div style={styles.wrapper}>
            <div style={styles.header}>
                <p style={styles.logo}>{profile?.name || 'SFARMS'}</p>
                <p style={styles.tagline}>Farm Management System</p>
                <span style={styles.badge}>{isPreview ? '⬡ Preview' : '✓ Sale Receipt'}</span>
            </div>

            <div style={styles.body}>
                <div style={styles.amountSection}>
                    <p style={styles.amountLabel}>Total Amount</p>
                    <p style={styles.amountValue}>{formatKES(totalAmount)}</p>
                    {formData?.paymentStatus && (
                        <span style={styles.statusBadge}>{isPaid ? '✓ Fully Paid' : isPartial ? '◑ Partial Payment' : '○ Pending'}</span>
                    )}
                </div>

                <div style={styles.section}>
                    <p style={styles.sectionTitle}>Transaction</p>
                    <div style={styles.row}>
                        <span style={styles.rowLabel}>Receipt No</span>
                        <span style={styles.rowValue}>#{receiptNum}</span>
                    </div>
                    {transactionId && (
                        <div style={styles.row}>
                            <span style={styles.rowLabel}>Transaction ID</span>
                            <span style={styles.rowValue}>{transactionId}</span>
                        </div>
                    )}
                    <div style={styles.row}>
                        <span style={styles.rowLabel}>Date</span>
                        <span style={styles.rowValue}>{formData?.saleDate ? dayjs(formData.saleDate).format('DD MMM YYYY') : 'N/A'}</span>
                    </div>
                    <div style={styles.row}>
                        <span style={styles.rowLabel}>Payment</span>
                        <span style={styles.rowValue}>{formData?.paymentMethod?.toUpperCase() || '—'}</span>
                    </div>
                    <div style={styles.row}>
                        <span style={styles.rowLabel}>Delivery</span>
                        <span style={styles.rowValue}>{formData?.deliveredMethod === 'delivered' ? 'Delivered' : 'Picked Up'}</span>
                    </div>
                </div>

                <div style={styles.section}>
                    <p style={styles.sectionTitle}>Customer</p>
                    <div style={styles.row}>
                        <span style={styles.rowLabel}>Name</span>
                        <span style={styles.rowValue}>{customerName}</span>
                    </div>
                </div>

                <div style={{ ...styles.section, borderBottom: 'none' }}>
                    <p style={styles.sectionTitle}>Items ({items.length})</p>
                    {items.map((item, i) => {
                        const desc = getItemDescription(item);
                        return (
                            <div key={i} style={styles.itemRow}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                                    <div style={{ flex: 1 }}>
                                        <p style={styles.itemName}>{desc.name}</p>
                                        {desc.meta && <p style={styles.itemMeta}>{desc.meta}</p>}
                                    </div>
                                    <p style={styles.itemSubtotal}>{formatKES(calculateSubtotal(item))}</p>
                                </div>
                            </div>
                        );
                    })}

                    <div style={styles.totalRow}>
                        <span style={styles.totalLabel}>Total</span>
                        <span style={styles.totalValue}>{formatKES(totalAmount)}</span>
                    </div>
                </div>

                <div style={styles.footer}>
                    <p style={styles.thankYou}>Thank You!</p>
                    <p style={styles.footerMeta}>
                        {profile?.phone1} · {profile?.email}
                    </p>
                    <p style={{ ...styles.footerMeta, marginTop: 4 }}>Keep this receipt for your records</p>
                </div>
            </div>

            <div style={styles.receiptNum}>*** {receiptNum} ***</div>
        </div>
    );
}
